import { useAuth0 } from '@auth0/auth0-react'
import { AlertProvider, Toaster } from '@dittolive/anvil'
import { QueryClientProvider } from '@tanstack/react-query'
import { ErrorComponent, isAuthError } from 'components/error'
import { FullPageLoader } from 'components/loader'
import RelayProvider from 'components/relay'
import { TenantContextProvider } from 'components/tenant'
import UserSync from 'components/userSync'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import { Suspense, useCallback, useEffect, useMemo } from 'react'
import { useNavigateRef } from 'utils/navigate'
import { queryClient } from 'utils/queryClient'

import AppRouter from './AppRouter'
import { AppUpdater } from './AppUpdater'
import AppContext from './context'

/** Entry point for the app, where we handle user authentication. */
function AppEntry() {
  const navigate = useNavigateRef()
  const {
    error,
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0()

  useEffect(() => {
    if (!isAuthenticated && !isLoading && !error) {
      if (window.location.pathname === '/create-account') {
        const searchParams = new URLSearchParams(window.location.search)
        const values = searchParams.get('values')
        loginWithRedirect({
          action: 'signup',
          values: values ?? undefined,
          appState: { redirectUrl: '/' },
        })
      } else {
        loginWithRedirect({
          action: 'login',
          appState: {
            redirectUrl: window.location.pathname,
            searchParams: window.location.search,
          },
        })
      }
    }
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    error,
  ])

  const getToken = useCallback(
    () => getAccessTokenSilently({ ignoreCache: false }),
    [getAccessTokenSilently],
  )

  const isUnverifiedEmailError = useMemo(
    () => error?.message?.toLowerCase() === 'email not verified',
    [error],
  )

  const authErrorPrimaryAction = useMemo(
    () => ({ label: 'Log out', onClick: logout }),
    [logout],
  )

  /**
   * An effect to redirect the user to the appropriate page if they have an expired or unverified email.
   *
   * Note: This should ideally handle other error cases, as well. Currently, it only handles the two cases.
   */
  useEffect(() => {
    if (isUnverifiedEmailError) {
      navigate('/email-verification-pending')
    }
  }, [isUnverifiedEmailError, navigate])

  const isAuthComplete = isAuthenticated && !isLoading && !error

  /**
   * An effect to remove any query params from the URL after the user has successfully logged in. Some
   * query params linger after the email confirmation flow, and while they don't cause any issues, they
   * are a bit of an eyesore.
   *
   * This was added as part of a fix for https://github.com/getditto/cloud-services/issues/2322
   */
  useEffect(() => {
    if (!isAuthComplete) return

    const searchParams = new URLSearchParams(window.location.search)

    const whitelistedParams = new URLSearchParams()
    searchParams.forEach((value, key) => {
      if (PORTAL_QUERY_PARAMS.includes(key)) {
        whitelistedParams.append(key, value)
      }
    })

    if (whitelistedParams.toString().length > 0) {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${whitelistedParams.toString()}`,
      )
    } else {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [isAuthComplete])

  if (isLoading) {
    return <FullPageLoader />
  }

  if (!isAuthenticated && !isLoading) {
    // Show an error page that lets the user log out if there was an authentication error.
    if (isAuthError(error)) {
      return (
        <ErrorComponent error={error} primaryAction={authErrorPrimaryAction} />
      )
    }

    // Let the ErrorFallback handle any other errors.
    if (error) {
      throw error
    }

    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RelayProvider token={getToken}>
        <Suspense fallback={<FullPageLoader />}>
          <UserSync user={user!}>
            {(authenticatedUser) => (
              <AlertProvider>
                <AppContext.Provider
                  value={{
                    jwtToken: getToken,
                    isSuperAdmin: !!authenticatedUser?.isSuperAdmin,
                  }}
                >
                  <TenantContextProvider>
                    <AppUpdater />
                    <AppRouter />
                    <Toaster expand visibleToasts={5} />
                  </TenantContextProvider>
                </AppContext.Provider>
              </AlertProvider>
            )}
          </UserSync>
        </Suspense>
      </RelayProvider>
    </QueryClientProvider>
  )
}

export default withLDProvider({
  clientSideID: window._env_.LD_CLIENT_ID,
  options: {
    application: {
      id: 'portal_frontend',
      version: window._env_.BUILD_VERSION,
    },
  },
  // NOTE: We are creating a default, anonymous context here with a fixed key in order to avoid creating
  // an abundance of anonymous contexts. Without this, LaunchDarkly would create an anonymous context with a
  // unique key, which would drive up MAU figures in LaunchDarkly. See https://github.com/getditto/cloud-services/issues/2187
  context: {
    anonymous: true,
    key: 'anonymous-user',
    kind: 'user',
  },
})(AppEntry)

export const PORTAL_QUERY_PARAMS = ['dql', 'collection', 'presence', 'query']
